<template>
  <div class="h-full flex flex-col items-center py-8 px-4">
      <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl w-full h-full pb-6 pt-14 flex flex-col">

      <!-- Header -->
      <button class="absolute w-8 h-8 left-5 top-5 " @click="$router.back()">
        <ColorImg src="/images/icons/back.png" class="bg-current w-full h-full" alt="" />
      </button>
      <div class="absolute left-16 text-2xl top-5 title-color ">Notifications</div>

      <!-- Bouton "Tout marquer comme lu" -->
      <div
        v-if="unreadCount > 0"
        class="absolute right-5 top-5  cursor-pointer"
        @click="markAll"
      >
        <ActionTrigger
          ref="markAllTrigger"
          dataKey="markAllNotificationsRead"
          :ignoreLoading="true"
          @success="onMarkAllSuccess"
        />
        <div class="flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-all">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
          </svg>
          <span>Tout lire</span>
        </div>
      </div>

      <!-- Liste des notifications -->
      <div class="flex-1 overflow-y-auto mt-4 px-2">
        <DataFetcher dataKey="notifications" ref="notifFetcher">
          <template #any="{ loading, data, loadingError, refresh }">

            <!-- Skeleton loading -->
            <div v-if="loading" class="p-4 space-y-4">
              <div v-for="i in 5" :key="i" class="shimmerContainer h-20 rounded-2xl w-full"></div>
            </div>

            <!-- Erreur -->
            <div v-else-if="loadingError" class="p-8 text-center">
              <p class="text-red-500 font-medium">Erreur lors du chargement</p>
              <button @click="refresh" class="buttonComponent small mt-4">Réessayer</button>
            </div>

            <!-- Vide -->
            <div v-else-if="!data?.notifications?.length" class="h-[60vh] flex flex-col items-center justify-center gap-3">
              <NoDataView />
              <p class="text-gray-400 text-sm">Aucune notification pour l'instant</p>
            </div>

            <!-- Notifications -->
            <div v-else>
              <!-- Compteur non lus -->
              <div v-if="data.count?.unread > 0" class="px-3 pb-3">
                <span class="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {{ data.count.unread }} non {{ data.count.unread > 1 ? 'lues' : 'lue' }}
                </span>
              </div>

              <div
                v-for="notif in data.notifications"
                :key="notif.id"
                class="border-b last:border-0 border-gray-100 transition-all"
                :class="!notif.isRead ? 'bg-primary/5 rounded-2xl mb-1' : ''"
              >
                <div
                  class="p-4 py-5 flex items-start gap-3 hover:bg-white/40 transition-colors rounded-2xl cursor-pointer"
                  @click="showNotification(notif, refresh)"
                >
                  <!-- Icône type -->
                  <div
                    class="w-11 min-w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0"
                    :class="getNotifBg(notif.type)"
                  >
                    <ColorImg
                      :src="getNotifIcon(notif.type)"
                      :class="['w-5 h-5', getNotifIconColor(notif.type)]"
                    />
                  </div>

                  <div class="w-full overflow-hidden">
                    <div class="flex items-start justify-between gap-3 w-full">
                      <div class="font-bold title-color text-base truncate" :class="!notif.isRead ? 'text-primary' : ''">
                        {{ notif.title }}
                      </div>
                      <div class="flex items-center gap-2 flex-shrink-0">
                        <!-- Point non lu -->
                        <div v-if="!notif.isRead" class="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
                        <div class="text-gray-400 text-xs whitespace-nowrap pt-0.5">
                          {{ formatDate(notif.createdAt) }}
                        </div>
                      </div>
                    </div>
                    <p class="text-gray-500 text-sm line-clamp-2 mt-0.5">{{ notif.message }}</p>
                  </div>
                </div>
              </div>
            </div>

          </template>
        </DataFetcher>

        <!-- Trigger mark single read (caché, appelé programmatiquement) -->
        <ActionTrigger
          ref="markOneTrigger"
          dataKey="markNotificationRead"
          :ignoreLoading="true"
          @success="onMarkOneSuccess"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import NoDataView from '@/features/data/presentation/components/NoDataView.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'

const notifFetcher = ref(null)
const markOneTrigger = ref(null)
const markAllTrigger = ref(null)

// Nb de notifications non lues (calculé depuis la réponse du DataFetcher)
const unreadCount = computed(() => {
  return notifFetcher.value?.data?.count?.unread ?? 0
})

// ---------- Helpers visuels ----------

function getNotifBg(type) {
  const map = {
    order: 'bg-blue-100',
    promotion: 'bg-purple-100',
    warning: 'bg-yellow-100',
    error: 'bg-red-100',
    success: 'bg-green-100',
  }
  return map[type] || 'bg-gray-100'
}

function getNotifIcon(type) {
  const map = {
    order: '/images/icons/orders.png',
    warning: '/images/icons/warning.png',
    error: '/images/icons/warning.png',
    success: '/images/icons/check.png',
  }
  return map[type] || '/images/icons/notification.png'
}

function getNotifIconColor(type) {
  const map = {
    order: 'bg-blue-500',
    promotion: 'bg-purple-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    success: 'bg-green-500',
  }
  return map[type] || 'bg-gray-500'
}

function formatDate(timestamp) {
  if (!timestamp) return ''
  // L'API retourne un timestamp en ms (nombre)
  const d = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp)
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ---------- Actions ----------

async function showNotification(notif, refresh) {
  // Marquer comme lu si pas encore fait (fire-and-forget)
  if (!notif.isRead && markOneTrigger.value) {
    markOneTrigger.value.trigger({ notificationId: notif.id })
    // Mise à jour optimiste locale
    notif.isRead = true
  }

  await window.customPrompt({
    title: notif.title,
    subTitle: notif.message,
    successText: 'Fermer',
  })
}

function markAll() {
  if (markAllTrigger.value) {
    markAllTrigger.value.trigger({})
  }
}

function onMarkOneSuccess() {
  // Rafraîchir silencieusement pour sync le count serveur
  notifFetcher.value?.refresh?.()
}

function onMarkAllSuccess() {
  notifFetcher.value?.refresh?.()
}
</script>

<script>
export default {
  name: 'RiderInbox'
}
</script>

<style scoped>
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
</style>
